import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRupeeSign, faUsers, faLink, faUnlock,
  faArrowTrendUp, faArrowTrendDown, faCity,
  faRefresh, faChartBar, faBullhorn, faHeartPulse,
  faRing, faShieldHalved, faHandHoldingDollar,
  faBell, faCircle, faChevronRight, faUserCheck,
  faUserXmark, faChevronDown, faChevronUp, faVenusMars,
} from "@fortawesome/free-solid-svg-icons";

const INSIGHTS_URL = "mst-admin/analytics/seeker-insights";

const WHO_FOR_COLORS = [
  "#6366F1", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16",
];

const STATS_URL = "mst-admin/dashboard/stats";
const REFRESH_INTERVAL_MS = 30_000;

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtRsDirect = (n) => `₹${(n ?? 0).toLocaleString("en-IN")}`;

// ─── Primitive helpers ────────────────────────────────────────────────────────

const Skeleton = ({ h = "h-4", w = "w-full", className = "" }) => (
  <div className={`${h} ${w} bg-slate-200 rounded-lg animate-pulse ${className}`} />
);

const StatPill = ({ label, value, color = "bg-slate-100 text-slate-600" }) => (
  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${color}`}>
    {value} <span className="font-normal opacity-70">{label}</span>
  </span>
);

const BarRow = ({ label, value, max, color, suffix = "", pctLabel }) => {
  const pct = max > 0 ? Math.max((value / max) * 100, value > 0 ? 1 : 0) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-500 w-28 truncate flex-shrink-0">{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className="text-xs font-semibold text-slate-700 w-20 text-right flex-shrink-0">
        {suffix}{fmt(value)}{pctLabel ? <span className="text-slate-400 font-normal"> · {pctLabel}</span> : null}
      </span>
    </div>
  );
};

const Sparkline = ({ data = [] }) => {
  if (data.length < 2)
    return (
      <div className="flex items-center justify-center h-14 text-xs text-slate-300">
        No revenue data yet
      </div>
    );
  const vals = data.map((d) => d.amount || 0);
  const max = Math.max(...vals, 1);
  const W = 300, H = 56;
  const pts = vals
    .map((v, i) => {
      const x = (i / (vals.length - 1)) * W;
      const y = H - 4 - ((v / max) * (H - 10));
      return `${x},${y}`;
    })
    .join(" ");
  const area = `0,${H} ` + pts + ` ${W},${H}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none">
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366F1" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#sparkFill)" />
      <polyline
        points={pts}
        fill="none"
        stroke="#6366F1"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, accent, icon, trend }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: `${accent}18` }}
      >
        <FontAwesomeIcon icon={icon} style={{ color: accent }} className="text-base" />
      </div>
      {trend !== undefined && (
        <span
          className={`text-xs font-semibold flex items-center gap-1 ${
            trend >= 0 ? "text-emerald-500" : "text-red-400"
          }`}
        >
          <FontAwesomeIcon
            icon={trend >= 0 ? faArrowTrendUp : faArrowTrendDown}
            className="text-[10px]"
          />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-400 mt-0.5 font-medium">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Funnel ───────────────────────────────────────────────────────────────────

const FUNNEL_STEPS = [
  { key: "registered", label: "Registered" },
  { key: "profileCreated", label: "Profile Created" },
  { key: "interestSent", label: "Interest Sent" },
  { key: "interestAccepted", label: "Accepted" },
  { key: "alignmentUnlocked", label: "Alignment Unlocked" },
];
const FUNNEL_COLORS = ["#6366F1", "#818CF8", "#A78BFA", "#C084FC", "#C9A84C"];

const PACK_CONFIG = [
  { key: "free", label: "Free", color: "#94A3B8" },
  { key: "spark_bundle", label: "Spark Bundle", color: "#6366F1" },
  { key: "premium", label: "Premium", color: "#7C3AED" },
  { key: "elite", label: "Elite", color: "#F59E0B" },
  { key: "annual", label: "Annual", color: "#059669" },
  { key: "single_unlock", label: "Single Unlock", color: "#60A5FA" },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const CommandDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const intervalRef = useRef(null);
  const tickRef = useRef(null);

  // Platform Insights state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insightsOpen, setInsightsOpen] = useState(true);

  const fetchStats = useCallback(
    async (silent = false, dateRange = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      const body = {};
      if (dateRange.startDate) body.startDate = dateRange.startDate;
      if (dateRange.endDate) body.endDate = dateRange.endDate;

      try {
        const res = await func.post(STATS_URL, body);
        if (res?.status === 200) {
          setStats(res.data);
          setLastUpdated(new Date());
          setSecondsAgo(0);
        } else {
          toast.error("Failed to load dashboard stats");
        }
      } catch {
        toast.error("Dashboard request failed");
      }
      setLoading(false);
      setRefreshing(false);
    },
    []
  );

  // Auto-refresh every 30 seconds
  useEffect(() => {
    fetchStats(false);
    intervalRef.current = setInterval(() => {
      fetchStats(true);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchStats]);

  // Tick the "X seconds ago" counter
  useEffect(() => {
    tickRef.current = setInterval(() => {
      setSecondsAgo((s) => s + 1);
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const handleApplyDateRange = () => {
    fetchStats(false, { startDate, endDate });
  };

  // Fetch Platform Insights once on mount
  useEffect(() => {
    const fetchInsights = async () => {
      setInsightsLoading(true);
      try {
        const res = await func.get(INSIGHTS_URL);
        if (res?.status === 200) {
          setInsights(res.data);
        }
      } catch {
        // silent — don't toast, just skip rendering
      } finally {
        setInsightsLoading(false);
      }
    };
    fetchInsights();
  }, []);

  // ── Destructure safely ────────────────────────────────────────────────────
  const today = stats?.today ?? {};
  const funnel = stats?.funnel ?? {};
  const revenue = stats?.revenue ?? {};
  const connections = stats?.connections ?? {};
  const users = stats?.users ?? {};
  const topCities = stats?.topCities ?? [];
  const interactions = stats?.interactions ?? {};
  const platform = stats?.platform ?? {};
  const packs = stats?.packs ?? {};
  const secondMarriage = stats?.secondMarriagePool ?? {};
  const broadcasts = stats?.broadcastAnalytics ?? {};
  const byPurpose = revenue?.byPurpose
    ? Object.entries(revenue.byPurpose).map(([k, v]) => ({ _id: k, amount: v }))
    : [];

  const funnelMax = funnel?.registered || 1;
  const packBreakdown = packs?.breakdown ?? {};
  const packTotal = packs?.total || 1;

  // Broadcast delivery rate
  const recentBroadcasts = broadcasts?.recentBroadcasts ?? [];
  const totalBroadcastReach = broadcasts?.totalReach ?? 0;
  const totalBroadcastCount = broadcasts?.totalBroadcasts ?? 0;
  const avgDelivery =
    recentBroadcasts.length > 0
      ? Math.round(
          recentBroadcasts.reduce((acc, b) => {
            const total = (b.sent || 0) + (b.failed || 0);
            return acc + (total > 0 ? ((b.sent || 0) / total) * 100 : 0);
          }, 0) / recentBroadcasts.length
        )
      : 0;

  const msStats = platform?.matchSeekers ?? {};
  const upgradeOpportunity = packs?.upgradeOpportunity ?? 0;

  return (
    <div className="min-h-screen">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Command Dashboard</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <FontAwesomeIcon
              icon={faCircle}
              className="text-[8px] text-emerald-500 animate-pulse"
            />
            <p className="text-xs text-slate-400">
              {lastUpdated
                ? `Updated ${secondsAgo}s ago`
                : "Loading…"}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Date range picker */}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="text-xs text-slate-600 outline-none border-none bg-transparent"
            />
            <span className="text-slate-300 text-xs">→</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="text-xs text-slate-600 outline-none border-none bg-transparent"
            />
            <button
              onClick={handleApplyDateRange}
              className="ml-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition"
            >
              Apply
            </button>
          </div>
          <button
            onClick={() => fetchStats(true, { startDate, endDate })}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition shadow-sm disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={faRefresh}
              className={`text-xs ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Section 1: Top KPI Strip ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100"
            >
              <Skeleton h="h-10" w="w-10" />
              <div className="mt-3 space-y-2">
                <Skeleton h="h-7" w="w-24" />
                <Skeleton h="h-3" w="w-32" />
              </div>
            </div>
          ))
        ) : (
          <>
            <KpiCard
              icon={faUsers}
              label="Total Match Seekers"
              value={fmt(users?.total)}
              sub={`${fmt(users?.newThisPeriod)} new this period`}
              accent="#6366F1"
            />
            <KpiCard
              icon={faChartBar}
              label="BG Profiles"
              value={fmt(platform?.bgProfiles?.total)}
              sub={`${fmt(users?.withProfile)} seekers have profiles`}
              accent="#7C3AED"
            />
            <KpiCard
              icon={faRupeeSign}
              label="Today's Revenue"
              value={fmtRsDirect(today?.revenue)}
              sub={`${fmt(today?.activeUsers)} active today`}
              accent="#10B981"
            />
            <KpiCard
              icon={faHeartPulse}
              label="Active Users (30d)"
              value={fmt(users?.active30d)}
              sub={`of ${fmt(users?.total)} total`}
              accent="#F59E0B"
            />
          </>
        )}
      </div>

      {/* ── Section 2: Pack Distribution ─────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-5">
          <div>
            <h2 className="font-semibold text-slate-800">User Pack Breakdown</h2>
            <p className="text-xs text-slate-400 mt-0.5">Monetization health</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatPill
              label="paid users"
              value={fmt(packs?.totalPaid)}
              color="bg-violet-50 text-violet-700"
            />
            <StatPill
              label="free users"
              value={fmt(packs?.totalFree)}
              color="bg-slate-100 text-slate-600"
            />
            <StatPill
              label="expiring in 7 days"
              value={fmt(packs?.expiringSoon)}
              color="bg-amber-50 text-amber-700"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} h="h-6" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {PACK_CONFIG.map(({ key, label, color }) => {
              const count = packBreakdown[key] ?? 0;
              const pct = packTotal > 0 ? ((count / packTotal) * 100).toFixed(1) : "0.0";
              return (
                <BarRow
                  key={key}
                  label={label}
                  value={count}
                  max={packTotal}
                  color={color}
                  pctLabel={`${pct}%`}
                />
              );
            })}
          </div>
        )}

        {/* Upgrade nudge alert */}
        {!loading && upgradeOpportunity > 0 && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100 rounded-xl p-4">
            <p className="text-sm font-semibold text-indigo-800">
              🎯 {fmt(upgradeOpportunity)} free users have been on the platform for 7+ days.{" "}
              <span className="font-normal text-indigo-600">Prime upgrade targets.</span>
            </p>
            <button
              onClick={() => navigate("/notifications/broadcast")}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition shadow-sm flex-shrink-0"
            >
              Send Upgrade Nudge
              <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
            </button>
          </div>
        )}
      </div>

      {/* ── Section 3: Funnel + Revenue ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 mb-6">
        {/* Conversion Funnel */}
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="mb-5">
            <h2 className="font-semibold text-slate-800">Conversion Funnel</h2>
            <p className="text-xs text-slate-400 mt-0.5">User journey through the platform</p>
          </div>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} h="h-6" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {FUNNEL_STEPS.map((step, i) => {
                const val = funnel[step.key] ?? 0;
                const prev = i > 0 ? funnel[FUNNEL_STEPS[i - 1].key] ?? 0 : val;
                const dropoff = i > 0 && prev > 0 ? prev - val : null;
                const pct = funnelMax > 0 ? Math.round((val / funnelMax) * 100) : 0;
                return (
                  <div key={step.key}>
                    <div className="flex justify-between items-center mb-1.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: FUNNEL_COLORS[i] }}
                        />
                        <span className="text-xs font-medium text-slate-600">
                          {step.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {dropoff !== null && dropoff > 0 && (
                          <span className="text-[10px] text-red-400 font-semibold bg-red-50 px-1.5 py-0.5 rounded-full">
                            -{fmt(dropoff)}
                          </span>
                        )}
                        <span className="text-xs font-bold text-slate-800">
                          {fmt(val)}
                        </span>
                        <span className="text-[10px] text-slate-400">({pct}%)</span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${Math.max(pct, val > 0 ? 2 : 0)}%`,
                          background: FUNNEL_COLORS[i],
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Revenue */}
        <div className="xl:col-span-3 bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h2 className="font-semibold text-slate-800">Revenue</h2>
              <p className="text-xs text-slate-400 mt-0.5">Platform earnings</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-800">
                {fmtRsDirect(revenue?.total)}
              </p>
              <p className="text-xs text-slate-400">
                {fmt(revenue?.successful)} of {fmt(revenue?.transactionCount)} txns successful
              </p>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} h="h-5" />
              ))}
            </div>
          ) : (
            <>
              {byPurpose.length > 0 && (
                <div className="space-y-3 mb-5">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    By Category
                  </p>
                  {byPurpose.map((b) => {
                    const bMax = Math.max(
                      ...byPurpose.map((x) => x.amount || 0),
                      1
                    );
                    return (
                      <BarRow
                        key={b._id}
                        label={(b._id ?? "Other").replace(/_/g, " ")}
                        value={b.amount ?? 0}
                        max={bMax}
                        color="#6366F1"
                        suffix="₹"
                      />
                    );
                  })}
                </div>
              )}
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">
                  30-Day Trend
                </p>
                <Sparkline data={revenue?.dailyChart ?? []} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Section 4: Connections + Interactions + Second Marriage ──────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        {/* Connections */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-1">
            <FontAwesomeIcon icon={faLink} className="text-slate-400 text-sm" />
            <h2 className="font-semibold text-slate-800">Connections</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5">Interest requests between profiles</p>
          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} h="h-5" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { label: "Total Sent", value: fmt(connections?.total), color: "text-slate-800" },
                  { label: "Accepted", value: fmt(connections?.accepted), color: "text-emerald-600" },
                  { label: "Declined", value: fmt(connections?.declined), color: "text-red-500" },
                  { label: "Pending", value: fmt(connections?.pending), color: "text-amber-500" },
                ].map((c) => (
                  <div key={c.label} className="bg-slate-50 rounded-xl p-3">
                    <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{c.label}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                {[
                  { label: "Accepted", value: connections?.accepted ?? 0, color: "#10B981" },
                  { label: "Pending", value: connections?.pending ?? 0, color: "#F59E0B" },
                  { label: "Declined", value: connections?.declined ?? 0, color: "#EF4444" },
                ].map((c) => {
                  const total =
                    (connections?.accepted ?? 0) +
                    (connections?.pending ?? 0) +
                    (connections?.declined ?? 0) || 1;
                  return (
                    <BarRow
                      key={c.label}
                      label={c.label}
                      value={c.value}
                      max={total}
                      color={c.color}
                    />
                  );
                })}
              </div>
              <div className="pt-3 border-t border-slate-100 mt-3 flex justify-between">
                <span className="text-xs text-slate-500">Acceptance Rate</span>
                <span className="text-xs font-bold text-emerald-500">
                  {connections?.acceptanceRate ?? "0.0%"}
                </span>
              </div>
            </>
          )}
        </div>

        {/* User Interactions */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-1">
            <FontAwesomeIcon icon={faChartBar} className="text-slate-400 text-sm" />
            <h2 className="font-semibold text-slate-800">User Interactions</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5">Engagement activity across profiles</p>
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} h="h-5" />
              ))}
            </div>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                {[
                  { label: "Profile Views", value: interactions?.totalViews ?? 0, color: "#6366F1" },
                  { label: "Passes (Skipped)", value: interactions?.passes ?? 0, color: "#94A3B8" },
                  { label: "Saved Profiles", value: interactions?.profileSaves ?? 0, color: "#C9A84C" },
                ].map((c) => {
                  const max = Math.max(
                    interactions?.totalViews ?? 0,
                    interactions?.passes ?? 0,
                    interactions?.profileSaves ?? 0,
                    1
                  );
                  return (
                    <BarRow
                      key={c.label}
                      label={c.label}
                      value={c.value}
                      max={max}
                      color={c.color}
                    />
                  );
                })}
              </div>
              {interactions?.passReasons &&
                Object.keys(interactions.passReasons).length > 0 && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">
                      Top Pass Reasons
                    </p>
                    {Object.entries(interactions.passReasons)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 3)
                      .map(([reason, count]) => (
                        <div
                          key={reason}
                          className="flex justify-between text-xs py-0.5"
                        >
                          <span className="text-slate-500 capitalize">
                            {reason.replace(/_/g, " ")}
                          </span>
                          <span className="font-semibold text-slate-700">
                            {fmt(count)}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
            </>
          )}
        </div>

        {/* Second Marriage Pool */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-1">
            <FontAwesomeIcon icon={faRing} className="text-slate-400 text-sm" />
            <h2 className="font-semibold text-slate-800">Second Marriage Pool</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5">Profiles open to remarriage</p>
          {loading ? (
            <div className="space-y-3">
              <Skeleton h="h-20" />
              <Skeleton h="h-4" />
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center justify-center py-6">
                <p className="text-6xl font-extrabold text-violet-600">
                  {fmt(secondMarriage?.count)}
                </p>
                <p className="text-sm text-slate-500 mt-2 text-center">
                  users in second marriage pool
                </p>
              </div>
              <div className="mt-2 bg-violet-50 rounded-xl p-3 text-center">
                <p className="text-xs text-violet-700 font-medium">
                  Dedicated pool segment for targeted notifications
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Section 5: Broadcast Analytics ────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FontAwesomeIcon icon={faBullhorn} className="text-slate-400 text-sm" />
              <h2 className="font-semibold text-slate-800">Broadcast History</h2>
            </div>
            <p className="text-xs text-slate-400">
              Last {recentBroadcasts.length} broadcasts sent
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatPill
              label="total broadcasts"
              value={fmt(totalBroadcastCount)}
              color="bg-indigo-50 text-indigo-700"
            />
            <StatPill
              label="total reach"
              value={fmt(totalBroadcastReach)}
              color="bg-emerald-50 text-emerald-700"
            />
            <StatPill
              label="avg delivery"
              value={`${avgDelivery}%`}
              color="bg-amber-50 text-amber-700"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} h="h-10" />
            ))}
          </div>
        ) : recentBroadcasts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center">
              <FontAwesomeIcon icon={faBell} className="text-slate-300 text-xl" />
            </div>
            <p className="text-sm text-slate-400">
              No broadcasts yet — send your first from the{" "}
              <button
                onClick={() => navigate("/notifications/broadcast")}
                className="text-indigo-500 font-semibold hover:underline"
              >
                Notifications page
              </button>
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left text-xs font-semibold text-slate-400 pb-3 pr-4">
                    Title
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-400 pb-3 pr-4">
                    Segment
                  </th>
                  <th className="text-right text-xs font-semibold text-slate-400 pb-3 pr-4">
                    Sent
                  </th>
                  <th className="text-right text-xs font-semibold text-slate-400 pb-3 pr-4">
                    Failed
                  </th>
                  <th className="text-right text-xs font-semibold text-slate-400 pb-3">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {recentBroadcasts.map((b, i) => {
                  const dateStr = b.createdAt
                    ? new Date(b.createdAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "—";
                  return (
                    <tr key={i} className="hover:bg-slate-50 transition">
                      <td className="py-3 pr-4 font-medium text-slate-700 max-w-xs truncate">
                        {b.title ?? "—"}
                      </td>
                      <td className="py-3 pr-4">
                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-semibold">
                          {b.segment ?? "All"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right text-emerald-600 font-semibold">
                        {fmt(b.sent)}
                      </td>
                      <td className="py-3 pr-4 text-right text-red-400 font-semibold">
                        {fmt(b.failed)}
                      </td>
                      <td className="py-3 text-right text-slate-400 text-xs">{dateStr}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Section 6: Top Cities + Platform Health ───────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
        {/* Top Cities */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-1">
            <FontAwesomeIcon icon={faCity} className="text-slate-400 text-sm" />
            <h2 className="font-semibold text-slate-800">Top Cities</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5">By profile count</p>
          {loading ? (
            <div className="space-y-3">
              {[...Array(7)].map((_, i) => (
                <Skeleton key={i} h="h-5" />
              ))}
            </div>
          ) : topCities.length === 0 ? (
            <div className="flex items-center justify-center h-24">
              <p className="text-sm text-slate-300">No city data yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topCities.slice(0, 8).map((c, i) => (
                <div key={c.city || i} className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-300 w-4 flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1">
                    <BarRow
                      label={c.city || "Unknown"}
                      value={c.count || 0}
                      max={topCities[0]?.count || 1}
                      color={`hsl(${220 + i * 18}, 70%, 60%)`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Platform Health */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-1">
            <FontAwesomeIcon icon={faShieldHalved} className="text-slate-400 text-sm" />
            <h2 className="font-semibold text-slate-800">Platform Health</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5">Operational status at a glance</p>
          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} h="h-14" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Support */}
              <div className="grid grid-cols-1 gap-3">
                <div
                  className={`rounded-xl p-4 ${
                    (platform?.support?.pending ?? 0) > 0
                      ? "bg-red-50 border border-red-100"
                      : "bg-emerald-50 border border-emerald-100"
                  }`}
                >
                  <p
                    className={`text-2xl font-bold ${
                      (platform?.support?.pending ?? 0) > 0
                        ? "text-red-600"
                        : "text-emerald-600"
                    }`}
                  >
                    {fmt(platform?.support?.pending)}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Support Pending</p>
                </div>
              </div>

              {/* Match Seekers Active vs Inactive */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                    Match Seeker Activity
                  </p>
                  <span className="text-xs font-bold text-slate-700">
                    {fmt(msStats?.total)} total
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center">
                    <FontAwesomeIcon
                      icon={faUserCheck}
                      className="text-emerald-500 text-base mb-1"
                    />
                    <p className="text-lg font-bold text-emerald-600">
                      {fmt(msStats?.active)}
                    </p>
                    <p className="text-[10px] text-slate-400">Active</p>
                  </div>
                  <div className="text-center">
                    <FontAwesomeIcon
                      icon={faUserXmark}
                      className="text-slate-400 text-base mb-1"
                    />
                    <p className="text-lg font-bold text-slate-500">
                      {fmt(msStats?.inactive)}
                    </p>
                    <p className="text-[10px] text-slate-400">Inactive</p>
                  </div>
                  <div className="text-center">
                    <FontAwesomeIcon
                      icon={faBell}
                      className="text-indigo-400 text-base mb-1"
                    />
                    <p className="text-lg font-bold text-indigo-600">
                      {fmt(msStats?.invited)}
                    </p>
                    <p className="text-[10px] text-slate-400">Invited</p>
                  </div>
                </div>
                {/* Active/Inactive bar */}
                {(msStats?.total ?? 0) > 0 && (
                  <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden flex">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{
                        width: `${((msStats?.active ?? 0) / (msStats?.total || 1)) * 100}%`,
                      }}
                    />
                    <div
                      className="h-full bg-indigo-400 transition-all"
                      style={{
                        width: `${((msStats?.invited ?? 0) / (msStats?.total || 1)) * 100}%`,
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Today quick stats */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    label: "Connections Today",
                    value: fmt(today?.connectionsSent),
                    color: "text-indigo-600",
                    icon: faLink,
                  },
                  {
                    label: "Accepted Today",
                    value: fmt(today?.connectionsAccepted),
                    color: "text-emerald-600",
                    icon: faUserCheck,
                  },
                  {
                    label: "Unlocks Today",
                    value: fmt(today?.alignmentUnlocks),
                    color: "text-amber-600",
                    icon: faUnlock,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="bg-slate-50 rounded-xl p-3 text-center"
                  >
                    <FontAwesomeIcon
                      icon={item.icon}
                      className={`text-sm mb-1 ${item.color}`}
                    />
                    <p className={`text-lg font-bold ${item.color}`}>
                      {item.value}
                    </p>
                    <p className="text-[10px] text-slate-400 leading-tight mt-0.5">
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* ── Section 7: Platform Insights ─────────────────────────────────────── */}
      {(insightsLoading || insights) && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          {/* Collapsible header */}
          <button
            onClick={() => setInsightsOpen((o) => !o)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition"
          >
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faVenusMars} className="text-indigo-500 text-sm" />
              <h2 className="font-semibold text-slate-800">Platform Insights</h2>
              <span className="text-xs text-slate-400 font-normal ml-1">BG profile composition</span>
            </div>
            <FontAwesomeIcon
              icon={insightsOpen ? faChevronUp : faChevronDown}
              className="text-slate-400 text-sm"
            />
          </button>

          {insightsOpen && (
            <div className="px-6 pb-6 space-y-8 border-t border-slate-50 pt-5">

              {insightsLoading ? (
                /* Loading skeleton */
                <div className="space-y-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="space-y-3">
                      <div className="h-4 w-40 bg-slate-200 rounded-lg animate-pulse" />
                      <div className="grid grid-cols-2 gap-4">
                        <div className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
                        <div className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* BG Profile Gender Split */}
                  {insights?.genderSplit && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
                        BG Profile Gender Split
                      </p>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        {/* Male card */}
                        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex flex-col items-center justify-center gap-1">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 mb-1">
                            Male
                          </span>
                          <p className="text-3xl font-extrabold text-blue-700">
                            {(insights.genderSplit.male ?? 0).toLocaleString("en-IN")}
                          </p>
                          <p className="text-xs text-blue-500 font-medium">
                            {insights.genderSplit.malePercent ?? "0"}%
                          </p>
                        </div>
                        {/* Female card */}
                        <div className="bg-pink-50 border border-pink-100 rounded-2xl p-4 flex flex-col items-center justify-center gap-1">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-pink-100 text-pink-700 mb-1">
                            Female
                          </span>
                          <p className="text-3xl font-extrabold text-pink-600">
                            {(insights.genderSplit.female ?? 0).toLocaleString("en-IN")}
                          </p>
                          <p className="text-xs text-pink-500 font-medium">
                            {insights.genderSplit.femalePercent ?? "0"}%
                          </p>
                        </div>
                      </div>
                      {/* Stacked bar */}
                      {(() => {
                        const total = (insights.genderSplit.male ?? 0) + (insights.genderSplit.female ?? 0);
                        const malePct = total > 0 ? ((insights.genderSplit.male ?? 0) / total) * 100 : 50;
                        return (
                          <div className="h-3 bg-pink-200 rounded-full overflow-hidden flex">
                            <div
                              className="h-full bg-blue-500 transition-all duration-700 rounded-full"
                              style={{ width: `${malePct}%` }}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Who Are Profiles Created For? */}
                  {insights?.whoForBreakdown && insights.whoForBreakdown.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
                        Who Are Profiles Created For?
                      </p>
                      <div className="space-y-3">
                        {(() => {
                          const maxVal = Math.max(...insights.whoForBreakdown.map((w) => w.count ?? 0), 1);
                          return insights.whoForBreakdown.map((item, i) => {
                            const color = WHO_FOR_COLORS[i % WHO_FOR_COLORS.length];
                            const pct = Math.max(((item.count ?? 0) / maxVal) * 100, item.count > 0 ? 2 : 0);
                            return (
                              <div key={item.label || i} className="flex items-center gap-3">
                                <span
                                  className="text-xs font-semibold w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-white"
                                  style={{ background: color }}
                                >
                                  {(item.label || "?")[0]}
                                </span>
                                <span className="text-xs text-slate-600 w-24 flex-shrink-0 truncate">
                                  {item.label || "Unknown"}
                                </span>
                                <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                                  <div
                                    className="h-full rounded-full transition-all duration-700"
                                    style={{ width: `${pct}%`, background: color }}
                                  />
                                </div>
                                <span className="text-xs font-bold text-slate-700 w-12 text-right flex-shrink-0">
                                  {(item.count ?? 0).toLocaleString("en-IN")}
                                </span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Second Marriage Pool Gender */}
                  {insights?.secondMarriageGenderSplit && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
                        Second Marriage Pool — Gender
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex flex-col items-center gap-1">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 mb-1">
                            Male
                          </span>
                          <p className="text-2xl font-extrabold text-blue-700">
                            {(insights.secondMarriageGenderSplit.male ?? 0).toLocaleString("en-IN")}
                          </p>
                          <p className="text-xs text-blue-500 font-medium">
                            {insights.secondMarriageGenderSplit.malePercent ?? "0"}%
                          </p>
                        </div>
                        <div className="bg-pink-50 border border-pink-100 rounded-2xl p-4 flex flex-col items-center gap-1">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-pink-100 text-pink-700 mb-1">
                            Female
                          </span>
                          <p className="text-2xl font-extrabold text-pink-600">
                            {(insights.secondMarriageGenderSplit.female ?? 0).toLocaleString("en-IN")}
                          </p>
                          <p className="text-xs text-pink-500 font-medium">
                            {insights.secondMarriageGenderSplit.femalePercent ?? "0"}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Approval Health */}
                  {insights?.approvalHealth && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
                        Approval Health
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 text-center">
                          <p className="text-2xl font-extrabold text-emerald-600">
                            {(insights.approvalHealth.approved ?? 0).toLocaleString("en-IN")}
                          </p>
                          <p className="text-xs text-emerald-500 mt-1 font-medium">Approved</p>
                        </div>
                        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-center">
                          <p className="text-2xl font-extrabold text-amber-600">
                            {(insights.approvalHealth.pending ?? 0).toLocaleString("en-IN")}
                          </p>
                          <p className="text-xs text-amber-500 mt-1 font-medium">Pending</p>
                        </div>
                        <div className="bg-red-50 border border-red-100 rounded-2xl p-4 text-center">
                          <p className="text-2xl font-extrabold text-red-500">
                            {(insights.approvalHealth.rejected ?? 0).toLocaleString("en-IN")}
                          </p>
                          <p className="text-xs text-red-400 mt-1 font-medium">Rejected</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Marital Status Breakdown */}
                  {insights?.maritalStatusBreakdown && insights.maritalStatusBreakdown.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
                        Marital Status Breakdown
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {insights.maritalStatusBreakdown.map((item, i) => (
                          <span
                            key={item.status || i}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700"
                          >
                            {item.status?.replace(/_/g, " ") || "Unknown"}
                            <span className="bg-white px-1.5 py-0.5 rounded-full text-slate-500 font-bold text-[10px]">
                              {(item.count ?? 0).toLocaleString("en-IN")}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommandDashboard;
